import './SignDocsListWithFilters.scss';
import React, {useState, useCallback, useContext, useMemo, useEffect} from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useLocation} from "react-router-dom";
import {SignDocsList} from "../SignDocsList/SignDocsList";
import {MySignDocsContent} from "../MySignDocsContent/MySignDocsContent";
import {MyArchivedSignDocsContent} from "../MyArchivedSignDocsContent/MyArchivedSignDocsContent";
import {SearchFilter} from "../../../Utils/GridFilters/SearchFilter/SerachFilter";
import {SignDocTypeFilter} from "../../../Utils/GridFilters/TypeFilter/SignDocTypeFilter";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {CompanyReactContext} from "../../../contexts/CompanyContext";
import {getAllSignDocTypes, getAvailableTypeOptions, VIDEO_TYPE, VIDEO_TYPES} from "../../SignDoc/SignDocLib";
import {SignDocCategories} from "../../../Utils/Constants";
import {get} from "lodash";
import {useFilterPersistence, generateFilterStorageKey, isInPersistableSection} from "../../../Hooks/useFilterPersistence"; 

/**
 * SignDocsListWithFilters - Generic wrapper component with filter support
 *
 * This component follows the same pattern as DataGridWrapper, using filterModel
 * to manage filter state and render existing filter components like SearchFilter.
 *
 * It conditionally renders either SignDocsList (admin view) or MySignDocsContent (user view)
 * based on the isUserView flag.
 *
 * @param {object} params - Component parameters
 * @param {Array} params.types - Array of SignDoc types to fetch (all types for API)
 * @param {Array} params.categories - Array of SignDoc categories to fetch
 * @param {object} params.showFilters - Object defining which filters to show (e.g., {search: true, type: true})
 * @param {Array} params.filterableTypes - Array of SignDoc types to show in type filter (filtered by feature flags)
 * @param {boolean} params.draggable - If true, makes SignDoc cards draggable (for learning paths)
 * @param {boolean} params.noButton - If true, hides action buttons on cards (used with draggable)
 * @param {Array} params.excludeSignDocIds - Array of SignDoc IDs to exclude from the list (e.g., docs already in learning path)
 * @param {boolean} params.isUserView - If true, renders MySignDocsContent (user view) instead of SignDocsList (admin view)
 * @param {boolean} params.showForceCompletion - (User view only) Show force completion SignDocs
 * @param {boolean} params.showNonForceCompletion - (User view only) Show non-force completion SignDocs
 * @param {boolean} params.hideLearningPathItems - (User view only) Hide learning path items
 * @param {boolean} params.showOnlyUncompletedTasks - (User view only) Show only uncompleted tasks (no completed section)
 * @param {boolean} params.isArchivedView - If true, renders MyArchivedSignDocsContent (user archived view)
 * @returns {JSX.Element} SignDocsListWithFilters component
 *
 * @example
 * // Admin usage (Learning Management)
 * <SignDocsListWithFilters
 *   categories={[FORCE_COMPLETION]}
 *   types={getAllTypes()}
 *   filterableTypes={availableSignDocTypes}
 *   showFilters={{search: true, type: true}}
 * />
 *
 * @example
 * // Admin draggable usage (Learning Path Creation)
 * <SignDocsListWithFilters
 *   types={getAllSignDocTypes()}
 *   categories={[FORCE_COMPLETION, KNOWLEDGE_CENTER]}
 *   filterableTypes={availableTypes}
 *   showFilters={{search: true, type: true}}
 *   draggable={true}
 *   noButton={true}
 *   excludeSignDocIds={[...signDocsAlreadyInPath]}
 * />
 *
 * @example
 * // User view usage (My SignDocs)
 * <SignDocsListWithFilters
 *   types={getAllTypes()}
 *   categories={[FORCE_COMPLETION]}
 *   showFilters={{search: true}}
 *   isUserView={true}
 *   showForceCompletion={true}
 *   showNonForceCompletion={false}
 * />
 */

/**
 * Whether a SignDoc row matches the search term, by name or description.
 *
 * Rows arrive in two shapes: an admin row carries the fields directly, a user row nests them
 * under `signDoc`. Both name and description are nullable columns, and lodash `get` returns its
 * default only for `undefined` - a stored `null` passes straight through. Every candidate is
 * therefore forced to a string before any string method touches it, because one null here throws
 * during render and the app has no error boundary to catch it.
 *
 * @param {object} params - The function parameters container object
 * @param {object} params.item - The SignDoc row, in either shape
 * @param {string} params.searchLower - The search term, already lowercased and trimmed
 * @returns {boolean} True when the name or the description contains the term
 */
export const signDocMatchesSearch = ({item, searchLower}) => {
    const docName = get(item, 'signDoc.docName') || get(item, 'docName') || '';
    const description = get(item, 'signDoc.description') || get(item, 'description') || '';

    return docName.toLowerCase().includes(searchLower) || description.toLowerCase().includes(searchLower);
};

export const SignDocsListWithFilters = ({
    types = getAllSignDocTypes(),
    categories = [SignDocCategories.FORCE_COMPLETION, SignDocCategories.CERTIFICATION],
    showFilters = {},
    filterableTypes = [],
    draggable = false,
    noButton = false,
    excludeSignDocIds = [],
    isUserView = false,
    showForceCompletion = false,
    showNonForceCompletion = false,
    hideLearningPathItems = true,
    showOnlyUncompletedTasks = false,
    showArchived = false,
    isArchivedView = false,
    disclaimerText = null
}) => {
    const {i18n} = useContext(I18nReactContext);
    const companyContext = useContext(CompanyReactContext);
    const location = useLocation();

    // Check if current route should have filter persistence (admin sections only)
    const shouldPersistFilters = useMemo(() => {
        return isInPersistableSection({pathname: location.pathname});
    }, [location.pathname]);

    /**
     * Prepare type options for the filter
     * Maps type codes to translated labels using i18n
     * Returns array of {value, text} objects (following MultiSelectAutocomplete pattern)
     * Uses filterableTypes if provided, otherwise falls back to all types
     *
     * IMPORTANT:
     * 1. Filters out types where Feature Flag is disabled
     * 2. Consolidates video types into a single "Videos" option
     *    - WATCH_AND_SIGN_YOUTUBE_LINK
     *    - WATCH_AND_SIGN_VIMEO_LINK
     *    - WATCH_AND_SIGN_VIDEO_FILE
     *    All three map to a single 'VIDEOS' filter option
     */
    const availableTypeOptions = useMemo(() => {
        return getAvailableTypeOptions({
            types: types,
            filterableTypes: filterableTypes,
            featureFlags: companyContext.featureFlags || {},
            i18n: i18n
        });
    }, [filterableTypes, types, i18n, companyContext.featureFlags]);

    // Generate unique storage key based on current route path
    const storageKey = useMemo(() => {
        return generateFilterStorageKey({basePath: location.pathname});
    }, [location.pathname]);

    // Default filter values when no persisted state exists
    // Unified structure: search, types, organizationIds (used by Reports, ignored here)
    const defaultFilterValues = useMemo(() => ({
        search: '',
        types: [],
        organizationIds: ['all']
    }), []);

    // Get persisted filter values from sessionStorage (only for admin sections)
    const { filterValues: persistedFilterValues, setFilterValues } = useFilterPersistence({
        storageKey,
        defaultFilterValues
    });

    // Filter model state - initialized with persisted values only for admin sections
    const [filterModel, setFilterModel] = useState({
        filterValues: shouldPersistFilters ? persistedFilterValues : defaultFilterValues,
        showFilters: showFilters
    });

    // Sync filter values to sessionStorage when they change (only for admin sections)
    useEffect(() => {
        if (shouldPersistFilters) {
            setFilterValues(filterModel.filterValues);
        }
    }, [filterModel.filterValues, setFilterValues, shouldPersistFilters]);

    /**
     * Filter SignDocs based on current filter model
     * Applies search and type filters with AND logic between them
     *
     * Handles both admin and user search patterns:
     * - Admin view: searches in signDoc.docName
     * - User view: searches in signDoc.signDoc.title and signDoc.signDoc.description
     *
     * @param {object} params - Function parameters
     * @param {Array} params.signDocs - Array of SignDoc objects to filter
     * @returns {Array} Filtered array of SignDocs
     */
    const applyFilters = useCallback(({signDocs}) => {
        if (!signDocs || signDocs.length === 0) {
            return signDocs;
        }

        let filteredDocs = signDocs;

        // Apply search filter if present
        const searchTerm = get(filterModel, 'filterValues.search', '');
        if (searchTerm && searchTerm.trim() !== '') {
            const searchLower = searchTerm.toLowerCase().trim();
            filteredDocs = filteredDocs.filter((item) => signDocMatchesSearch({item, searchLower}));
        }

        // Apply type filter if present (OR logic within types)
        const selectedTypes = get(filterModel, 'filterValues.types', []);
        if (selectedTypes && selectedTypes.length > 0) {

            // Expand 'VIDEOS' to include all individual video types
            const expandedTypes = selectedTypes.flatMap(type =>
                type === VIDEO_TYPE ? VIDEO_TYPES : [type]
            );

            // selectedTypes is already an array of type value strings (e.g., ['REGULAR_COURSE', 'EXAM'])
            filteredDocs = filteredDocs.filter((item) => {
                const type = get(item, 'signDoc.type') || get(item, 'type');
                return expandedTypes.includes(type);
            });
        }

        return filteredDocs;
    }, [filterModel]);

    return (
        <div className="SignDocsListWithFilters">
            {disclaimerText && (
                <div className="SignDocsListWithFilters__disclaimer">
                    <InfoOutlinedIcon fontSize="small" />
                    {disclaimerText}
                </div>
            )}
            {/* Render filters container if any filters are enabled */}
            {showFilters && Object.keys(showFilters).length > 0 && (
                <div className="SignDocsListWithFilters__filters-container">
                    {/* Search filter */}
                    {showFilters.search && (
                        <div className="SignDocsListWithFilters__filter">
                            <SearchFilter
                                filterModel={filterModel}
                                setFilterModel={setFilterModel}
                                label={i18n.translate('LearningManagementPage.searchTasks')}
                                placeholder={i18n.translate('LearningManagementPage.searchTasksPlaceholder')}
                            />
                        </div>
                    )}

                    {/* Type filter */}
                    {showFilters.type && (
                        <div className="SignDocsListWithFilters__filter">
                            <SignDocTypeFilter
                                filterModel={filterModel}
                                setFilterModel={setFilterModel}
                                availableTypes={availableTypeOptions}
                                label={i18n.translate('LearningManagementPage.filterByType')}
                            />
                        </div>
                    )}

                    {/* Future filters can be added here following the same pattern */}
                </div>
            )}

            {/* Conditionally render admin, user, or archived view */}
            {isArchivedView ? (
                <MyArchivedSignDocsContent
                    types={types}
                    categories={categories}
                    filterSignDocsBySearch={applyFilters}
                />
            ) : isUserView ? (
                <MySignDocsContent
                    types={types}
                    categories={categories}
                    filterSignDocsBySearch={applyFilters}
                    showForceCompletion={showForceCompletion}
                    showNonForceCompletion={showNonForceCompletion}
                    hideLearningPathItems={hideLearningPathItems}
                    showOnlyUncompletedTasks={showOnlyUncompletedTasks}
                />
            ) : (
                <SignDocsList
                    types={types}
                    categories={categories}
                    filterSignDocsBySearch={applyFilters}
                    draggable={draggable}
                    noButton={noButton}
                    excludeSignDocIds={excludeSignDocIds}
                    showArchived={showArchived}
                />
            )}
        </div>
    );
};

